// ./src/components/features/SignupItems/SignupItemEditDrawer/SignupItemEditDrawer.tsx
//
// Create/edit drawer for an event signup-list item (something guests sign up to bring
// or do). Self-loading in edit mode via recordId; context-or-props resolution; no
// success toast (the page highlight is the confirmation). Fields: name, type (category),
// description (markdown), external link, price + currency + unit, quantity available,
// max per guest, status, and — once saved — a main image + extra images.
// Mirrors AccommodationEditDrawer.

import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from "react";
import { Stack, TextInput, NumberInput, Input, Skeleton, Divider } from "@mantine/core";

import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { SignupItem, SignupItemInput, signupItemImageUrl, eventContextPath } from "@/utils/api";
import { getFormErrors, formatErrors } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { useOptionalSignupItemsContext } from "@/contexts/SignupItemsContext";
import { AppDrawer, StatusInput, CategorySelect, useCurrencyAffixes, MediaGalleryField, MediaImageRef } from "@/components/generic";

const MarkdownField = React.lazy(() =>
  import("@/components/generic/MarkdownField/MarkdownField").then((m) => ({ default: m.MarkdownField }))
);

export interface SignupItemEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** The event this item belongs to — set on the input in CREATE mode only. */
  eventId?: string | null;
  /** A name for a NEW item (the create wizard's one-tap ideas, "Dessert"). Ignored when editing. */
  defaultName?: string | null;
  /** #140 — how many a NEW item starts with (a gift list: 1, so one guest takes each gift). Ignored when editing. */
  defaultQuantity?: number | null;
  onCreated?: (item: SignupItem) => void;
  onUpdated?: (item: SignupItem) => void;
}

interface FormData {
  categoryId: string | null;
  name: string;
  description: string;
  link: string;
  price: number | "";
  priceUnit: string;
  quantityAvailable: number | "";
  maxQuantityPerGuest: number | "";
  photos: MediaImageRef[];
  status: number;
}

const KNOWN_FORM_FIELDS = [
  "categoryId",
  "name",
  "description",
  "link",
  "price",
  "priceUnit",
  "quantityAvailable",
  "maxQuantityPerGuest",
  "status",
];

const getInitialFormData = (defaultName?: string | null, defaultQuantity?: number | null): FormData => ({
  categoryId: null,
  name: defaultName ?? "",
  description: "",
  link: "",
  price: "",
  priceUnit: "",
  quantityAvailable: defaultQuantity ?? "",
  maxQuantityPerGuest: "",
  photos: [],
  status: STATUSES.ACTIVE,
});

const photosFromRecord = (record: SignupItem): MediaImageRef[] =>
  (record.images ?? []).map((i) => ({
    uploadedFileId: i.uploadedFileId ?? null,
    url: signupItemImageUrl(i),
  }));

const fromRecord = (record: SignupItem): FormData => ({
  categoryId: record.categoryId || null,
  name: record.name || "",
  description: record.description || "",
  link: record.link || "",
  price: record.price ?? "",
  priceUnit: record.priceUnit || "",
  quantityAvailable: record.quantityAvailable ?? "",
  maxQuantityPerGuest: record.maxQuantityPerGuest ?? "",
  photos: photosFromRecord(record),
  status: record.status ?? STATUSES.ACTIVE,
});

const serialize = (data: FormData): string => JSON.stringify(data);

const SignupItemEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={120} radius="sm" />
    <Skeleton height={60} radius="sm" />
  </Stack>
);

export const SignupItemEditDrawer: React.FC<SignupItemEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  eventId,
  onCreated,
  onUpdated,
  defaultName,
  defaultQuantity,
}) => {
  const { t } = useTranslation(["events", "common"]);
  // #A4.7b — the price is in the EVENT's currency, shown on the input (sign before, code after); not editable here.
  const currencyAffixes = useCurrencyAffixes();

  const ctx = useOptionalSignupItemsContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading ──
  const [record, setRecord] = useState<SignupItem | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as SignupItem);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  const effectiveError = loadError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record ? fromRecord(record) : getInitialFormData(defaultName, defaultQuantity);
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    clearErrRef.current?.();
  }, [opened, record, isEditMode, defaultName, defaultQuantity]);

  const setField = useCallback((field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);

    const submitData: SignupItemInput = {
      categoryId: formData.categoryId,
      name: formData.name.trim() || null,
      description: formData.description.trim() || null,
      link: formData.link.trim() || null,
      price: formData.price === "" ? null : Number(formData.price),
      priceUnit: formData.priceUnit.trim() || null,
      quantityAvailable: formData.quantityAvailable === "" ? null : Number(formData.quantityAvailable),
      maxQuantityPerGuest: formData.maxQuantityPerGuest === "" ? null : Number(formData.maxQuantityPerGuest),
      images: formData.photos.map((i) => ({
        uploadedFileId: i.uploadedFileId ?? null,
        // For an uploaded/library image, send only the ref (the server resolves the
        // URL); keep the raw URL for stock/URL picks.
        url: i.uploadedFileId ? null : i.url ?? null,
      })),
      status: formData.status,
    };

    if (!isEditMode) {
      submitData.eventId = eventId ?? null;
    }

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as SignupItem[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [formData, isEditMode, recordId, eventId, createFn, updateFn, onClose, onCreated, onUpdated]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened,
  });

  const drawerTitle = isEditMode
    ? t("events:signupItems.form.editTitle", { defaultValue: "Edit item" })
    : t("events:signupItems.form.addTitle", { defaultValue: "Add item" });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="md"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <SignupItemEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          <TextInput
            label={t("events:signupItems.form.fields.name.label", { defaultValue: "Name" })}
            placeholder={t("events:signupItems.form.fields.name.placeholder", {
              defaultValue: "e.g. Green salad, or Setup crew (2–4pm)",
            })}
            value={formData.name}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setField("name", value);
            }}
            error={fieldErrors.name}
            required
            data-autofocus
            size="md"
          />

          <CategorySelect
            group="signup"
            exactGroup
            label={t("events:signupItems.form.fields.category.label", { defaultValue: "Category" })}
            description={t("events:signupItems.form.fields.category.description", {
              defaultValue: "Food & drink, volunteer, supplies, merch…",
            })}
            value={formData.categoryId}
            onChange={(id) => setField("categoryId", id)}
            error={fieldErrors.categoryId}
            clearable
            size="md"
          />

          <Input.Wrapper
            label={t("events:signupItems.form.fields.description.label", { defaultValue: "Description" })}
            error={fieldErrors.description}
          >
            <Suspense fallback={<Skeleton height={180} radius="sm" />}>
              <MarkdownField
                value={formData.description}
                onChange={(md) => setField("description", md)}
                placeholder={t("events:signupItems.form.fields.description.placeholder", {
                  defaultValue: "Any details guests should know…",
                })}
                minHeight={180}
              />
            </Suspense>
          </Input.Wrapper>

          <TextInput
            label={t("events:signupItems.form.fields.link.label", { defaultValue: "Website link" })}
            placeholder={t("events:signupItems.form.fields.link.placeholder", {
              defaultValue: "https://… (product page, details, form)",
            })}
            value={formData.link}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setField("link", value);
            }}
            error={fieldErrors.link}
            size="md"
          />

          <NumberInput
            label={t("events:signupItems.form.fields.quantityAvailable.label", { defaultValue: "Quantity available" })}
            description={t("events:signupItems.form.fields.quantityAvailable.description", {
              defaultValue: "Leave empty for unlimited.",
            })}
            placeholder={t("events:signupItems.form.fields.quantityAvailable.placeholder", { defaultValue: "Unlimited" })}
            value={formData.quantityAvailable}
            onChange={(value) => setField("quantityAvailable", value === "" ? "" : (value as number))}
            error={fieldErrors.quantityAvailable}
            min={0}
            allowDecimal={false}
            step={1}
            size="md"
          />

          <NumberInput
            label={t("events:signupItems.form.fields.maxQuantityPerGuest.label", { defaultValue: "Max per guest" })}
            description={t("events:signupItems.form.fields.maxQuantityPerGuest.description", {
              defaultValue: "Leave empty for no limit.",
            })}
            placeholder={t("events:signupItems.form.fields.maxQuantityPerGuest.placeholder", { defaultValue: "No limit" })}
            value={formData.maxQuantityPerGuest}
            onChange={(value) => setField("maxQuantityPerGuest", value === "" ? "" : (value as number))}
            error={fieldErrors.maxQuantityPerGuest}
            min={1}
            allowDecimal={false}
            step={1}
            size="md"
          />

          <Divider
            label={t("events:signupItems.form.pricing.section", { defaultValue: "Pricing (optional)" })}
            labelPosition="left"
          />

          <NumberInput
            label={t("events:signupItems.form.fields.price.label", { defaultValue: "Price" })}
            value={formData.price}
            onChange={(value) => setField("price", value === "" ? "" : (value as number))}
            error={fieldErrors.price}
            min={0}
            decimalScale={2}
            step={0.01}
            thousandSeparator=","
            size="md"
            {...currencyAffixes}
          />

          <TextInput
            label={t("events:signupItems.form.fields.priceUnit.label", { defaultValue: "Price unit" })}
            placeholder={t("events:signupItems.form.fields.priceUnit.placeholder", { defaultValue: "per item" })}
            value={formData.priceUnit}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setField("priceUnit", value);
            }}
            error={fieldErrors.priceUnit}
            size="md"
          />

          <StatusInput
            value={formData.status}
            onChange={(status) => setField("status", status)}
            error={fieldErrors.status}
          />

          <Divider
            label={t("events:signupItems.form.photos.section", { defaultValue: "Images" })}
            labelPosition="left"
          />

          <MediaGalleryField
            value={formData.photos}
            onChange={(photos) => setField("photos", photos)}
            contextPath={eventId ? eventContextPath(eventId) : null}
            label={t("events:signupItems.form.photos.label", { defaultValue: "Photos" })}
            description={t("events:signupItems.form.photos.hint", {
              defaultValue: "The first photo is shown as the main image on your event website.",
            })}
          />
        </Stack>
      )}
    </AppDrawer>
  );
};
